import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { DefaultEditableActionItemContentCSS, IModal } from '../../../../../models';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../../models/NameUtils';
import { convertRawRichTextContentStateToRichContentEditorState } from '../../../../../models/UiUtils';
import { dateInputToIsoFormat, isoToDateInputFormat } from '../../../../../models/utils/dateUtils';
import {
	invalidateAllGetBoardItems,
	useCreateOpportunityMutation,
	useUpdateOpportunityMutation,
} from '../../../../../queries';
import { useCompanyAutocomplete } from '../../../../../queries/Company/useCompanyAutocomplete';
import { useContactAutocomplete } from '../../../../../queries/Contact/useContactAutocomplete';
import { useUserQuery } from '../../../../../queries/User/useUserQuery';
import { Button } from '../../../../components-v2/Button';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxList,
} from '../../../../components-v2/Combobox';
import {
	ComboboxAvatarItem,
	ComboboxAvatarItemAvatar,
	ComboboxAvatarItemDescription,
	ComboboxAvatarItemTitle,
} from '../../../../components-v2/Combobox/ComboboxAvatarItem';
import { DateInput } from '../../../../components-v2/DateInput';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../components-v2/Dialog';
import { Input } from '../../../../components-v2/Input';
import { InputGroupAddon } from '../../../../components-v2/InputGroup';
import { CurrencyInput } from '../../CurrencyInput';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../../components-v2/Select';
import { THEME } from '../../../../components-v2/Theme/theme';
import { baseStyleSheet } from '../../../../styles/styles';
import { ISelectOption } from '../../../DeprecatedSelect';
import { AddEntityButtons } from '../../../entities/AddEntityButtons';
import { TagsListWithTagsEditor } from '../../../entities/tags/TagsEditor';
import { InputFieldError } from '../../../InputFieldError';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { QuickAddEntity } from '../../../QuickAddEntity';
import {
	IRichContentDocumentEditorConfig,
	RichContentDocumentEditor,
} from '../../../richContent/RichContentDocumentEditor';
import { AssigneeIcon } from '../../../svgs/icons/AssigneeIcon';
import { ClearFieldIcon } from '../../../svgs/icons/ClearFieldIcon';
import { CompanyIcon } from '../../../svgs/icons/CompanyIcon';
import { TagIcon } from '../../../svgs/icons/TagIcon';
import { UserCombobox, UserComboboxAvatarItem, UserComboboxStatus } from '../../../UserCombobox';
import { styleSheet } from './styles';

const DefaultDetailsEditorConfig: IRichContentDocumentEditorConfig = {
	autoresizeToFitContent: true,
	contentRawCss: DefaultEditableActionItemContentCSS,
	plugins: [], // none
	toolbar: false,
};

const initials = (entity: { firstName?: string; lastName?: string }) =>
	`${entity.firstName?.substring(0, 1) ?? ''}${entity.lastName?.substring(0, 1) ?? ''}`;

const _EditOpportunityModal = ({
	opportunity,
	boards,
	parentModal,
	onSave,
	onCancel,
	initialStage,
	initialBoard,
}: {
	opportunity: Api.IOpportunity;
	boards?: Api.IBoard[];
	parentModal?: IModal;
	onSave: (opportunity: Api.IOpportunity) => void;
	onCancel: () => void;
	initialStage?: Api.IBoardStage;
	initialBoard?: Api.IBoard;
}) => {
	const userSession = useUserSession();

	const [amountErrorMessage, setAmountErrorMessage] = React.useState<string>('');
	const [amount, setAmount] = React.useState<number | null>(opportunity.dealSize ?? null);
	const [board, setBoard] = React.useState<Api.IBoard>(initialBoard ?? opportunity.boardStage?.board ?? boards?.[0]);
	const [closeDate, setCloseDate] = React.useState<Date>(
		opportunity.closeDate ? new Date(opportunity.closeDate) : null
	);
	const [closingDateErrorMessage, setClosingDateErrorMessage] = React.useState<string>('');
	const [company, setCompany] = React.useState<Api.ICompany>(opportunity.company ?? null);
	const [companyErrorMessage, setCompanyErrorMessage] = React.useState<string>('');
	const [companySearch, setCompanySearch] = React.useState<string>(opportunity.company?.companyName ?? '');
	const [contactSearch, setContactSearch] = React.useState<string>(
		opportunity.primaryContact ? getDisplayName(opportunity.primaryContact) : ''
	);
	const [detailsEditorState, setDetailsEditorState] = React.useState<Api.IRichContentEditorState>(null);
	const [name, setName] = React.useState<string>(opportunity.name ?? '');
	const [nameErrorMessage, setNameErrorMessage] = React.useState<string>('');
	const [owner, setOwner] = React.useState<Api.IUser>(opportunity.assignees?.[0] ?? null);
	const [ownerErrorMessage, setOwnerErrorMessage] = React.useState<string>('');
	const [primaryContact, setPrimaryContact] = React.useState<Api.IContact>(opportunity.primaryContact ?? null);
	const [primaryContactErrorMessage, setPrimaryContactErrorMessage] = React.useState<string>('');
	const [stage, setStage] = React.useState<Api.IBoardStage>(null);
	const [stageErrorMessage, setStageErrorMessage] = React.useState<string>('');
	const [tags, setTags] = React.useState<string[]>(opportunity.tags ?? []);

	// Quick-add ("Add a person" / "Add a company") reuses the shared QuickAddEntity flow, matching the
	// legacy autocomplete search field this modal replaces.
	const quickAddEntityVM = React.useMemo(() => new Api.QuickAddEntityViewModel(userSession), [userSession]);

	const contactAutocomplete = useContactAutocomplete({
		fragment: contactSearch,
		pageSize: 5,
		// Skip searching while the input still shows the already-selected contact (mount + post-select writeback).
		enabled: Boolean(contactSearch) && (!primaryContact || contactSearch !== getDisplayName(primaryContact)),
		searchFields: 'nameParts',
	});
	const contactItems = React.useMemo(
		() => contactAutocomplete.data?.pages.flatMap(page => page.values ?? []) ?? [],
		[contactAutocomplete.data]
	);

	const companyAutocomplete = useCompanyAutocomplete({
		fragment: companySearch,
		pageSize: 5,
		enabled: Boolean(companySearch) && (!company || companySearch !== (company.companyName ?? '')),
	});
	const companyItems = React.useMemo(
		() => companyAutocomplete.data?.pages.flatMap(page => page.values ?? []) ?? [],
		[companyAutocomplete.data]
	);

	const updateOpportunityMutation = useUpdateOpportunityMutation({
		onSuccess: () => invalidateAllGetBoardItems(),
	});

	const createOpportunityMutation = useCreateOpportunityMutation({
		onSuccess: () => invalidateAllGetBoardItems(),
	});

	React.useEffect(() => {
		setDetailsEditorState(convertRawRichTextContentStateToRichContentEditorState(opportunity.details));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		if (board || !boards?.length) return;
		setBoard(initialBoard ?? opportunity.boardStage?.board ?? boards[0]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [boards]);

	const isCreate = !opportunity.id;
	const defaultAssigneeId = board?.defaultAssigneeIds?.[0];
	const shouldLoadDefaultAssignee = isCreate && !owner && Boolean(defaultAssigneeId);
	const defaultAssigneeQuery = useUserQuery({
		id: defaultAssigneeId ?? '',
		enabled: shouldLoadDefaultAssignee,
		meta: { toastErrorMessage: 'Failed to load users' },
	});
	const hasInitializedOwnerRef = React.useRef(false);

	React.useEffect(() => {
		if (hasInitializedOwnerRef.current || !isCreate || owner) {
			return;
		}
		const defaultUser = defaultAssigneeQuery.data;
		if (!defaultUser) return;
		setOwner(defaultUser);
		hasInitializedOwnerRef.current = true;
	}, [isCreate, owner, defaultAssigneeQuery.data]);

	const onInputFocus = () => {
		clearErrorMessages();
	};

	const onCloseDateChange = (value: string) => {
		const iso = dateInputToIsoFormat(value);
		setCloseDate(iso ? new Date(iso) : null);
		clearErrorMessages();
	};

	const clearErrorMessages = () => {
		setAmountErrorMessage(null);
		setClosingDateErrorMessage(null);
		setCompanyErrorMessage(null);
		setNameErrorMessage(null);
		setOwnerErrorMessage(null);
		setPrimaryContactErrorMessage(null);
		setStageErrorMessage(null);
	};

	const validateInputs = () => {
		if (!primaryContact) setPrimaryContactErrorMessage('A primary contact is required.');
		if (!owner) setOwnerErrorMessage('An owner is required.');
		if (!name) setNameErrorMessage('An opportunity name is required.');
		if (!stage) setStageErrorMessage('An opportunity stage is required.');

		if (!primaryContact || !owner || !name || !stage) return false;

		return true;
	};

	const onSaveButtonClicked = async () => {
		if (!validateInputs()) return;

		const companyId = company ? company.id : primaryContact.companyId;
		const stageId = (stage || {}).id;

		const opportunityModel: Api.IOpportunity = {
			assignees: [{ id: owner.id }],
			boardStage: { id: stageId },
			closeDate: closeDate ? closeDate.toISOString() : null,
			company: { id: companyId },
			dealSize: amount ?? 0,
			details: detailsEditorState ? detailsEditorState.getRawRichTextContent() : null,
			name,
			primaryContact: { id: primaryContact.id },
			tags,
		};

		if (!!opportunity && !!opportunity.id) {
			opportunityModel.id = opportunity.id;
			await updateOpportunityMutation.mutateAsync(opportunityModel);
		} else {
			await createOpportunityMutation.mutateAsync(opportunityModel);
		}
		onSave(opportunityModel);
	};

	const onCompanySelected = (newCompany: Api.ICompany | null) => {
		setCompany(newCompany);
		// Set the input text explicitly: the Combobox only writes the label back on a user click, not on a
		// programmatic selection from the "Add a company" quick-add flow.
		setCompanySearch(newCompany?.companyName ?? '');
		clearErrorMessages();
	};

	const onOwnerSelected = (newOwner: Api.IUser | null) => {
		setOwner(newOwner);
		clearErrorMessages();
	};

	const onPrimaryContactSelected = (newContact: Api.IContact | null) => {
		if (!newContact) {
			setPrimaryContact(null);
			setContactSearch('');
			clearErrorMessages();
			return;
		}
		setPrimaryContact(newContact);
		// Seed company from the contact only when it actually has one — otherwise we'd set a truthy
		// { id: undefined } company that serializes as company: { id: undefined }.
		if (newContact.companyId) {
			setCompany({ companyName: newContact.companyName, id: newContact.companyId });
			setCompanySearch(newContact.companyName ?? '');
		}
		// Seed the opportunity name from the contact only when the user hasn't already entered one.
		setName(prev => prev || `${newContact.firstName ?? ''} ${newContact.lastName ?? ''}`.trim());
		// Set the input text explicitly: the Combobox only writes the label back on a user click, not on a
		// programmatic selection from the "Add a person" quick-add flow.
		setContactSearch(getDisplayName(newContact));
		clearErrorMessages();
	};

	const onStageSelected = (newStage: ISelectOption<Api.OpportunitiesBoardStageViewModel>) => {
		setStage(newStage.dataContext);
	};

	const onBoardSelected = (boardOption: ISelectOption<Api.IBoard>) => {
		const newBoard = boardOption.dataContext;
		setBoard(newBoard);
	};

	const onRequestClose = (result?: Api.OpportunityViewModel, canceled?: boolean) => () => {
		if (parentModal) {
			parentModal.onRequestClose(result, canceled);
		}
		if (canceled) {
			onCancel();
		}
	};

	const onRequestAddTag = (tag: Api.IAccountTag) => {
		if (!tags.includes(tag.name)) {
			setTags([...tags, tag.name]);
		}
	};

	const onRequestRemoveTag = (tag: Api.IAccountTag) => {
		setTags(tags.filter(t => t !== tag.name));
	};

	const loadedBoard = board ? boards?.find(loadedBoards => loadedBoards.id === board.id) : undefined;

	React.useEffect(() => {
		if (!loadedBoard) return;

		// If the current stage is not found in the board, set the first stage and return
		if (stage && !loadedBoard.stages.find(s => s.id === stage.id)) {
			setStage(loadedBoard.stages[0]);
			return;
		}

		// If the initial stage is not set, try to come up with the best default stage
		const initialStageObj = initialStage ?? opportunity.boardStage ?? initialBoard?.stages[0] ?? boards?.[0].stages[0];
		const matchingStage = loadedBoard.stages.find(s => s.id === initialStageObj.id);
		setStage(matchingStage);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loadedBoard]);

	const title = `${opportunity.id ? 'Edit' : 'Add New'} Opportunity`;

	if (!loadedBoard) {
		return (
			<Dialog
				open
				onOpenChange={open => {
					if (!open) onRequestClose(null, true)();
				}}
			>
				<DialogPopup styles={[styleSheet.popup]}>
					<DialogHeader>
						<DialogTitle>{title}</DialogTitle>
					</DialogHeader>
					<DialogBody>
						<div className={css(styleSheet.loading)}>
							<LoadingSpinner />
						</div>
					</DialogBody>
				</DialogPopup>
			</Dialog>
		);
	}

	const stageOptions: ISelectOption<Api.IBoardStage>[] = loadedBoard.stages.map(s => ({
		dataContext: s,
		id: s.id,
		text: s.name,
	}));

	const selectedStageOption = stage
		? {
				dataContext: stage,
				id: stage.id,
				text: stage.name,
			}
		: stageOptions[0];

	const defaultBoardOptions: ISelectOption<Api.IBoard>[] = [{ dataContext: board, id: board.id, text: board.name }];
	const boardOptions: ISelectOption<Api.IBoard>[] = boards
		? boards.map(boardOption => {
				return { dataContext: boardOption, id: boardOption.id, text: boardOption.name };
			})
		: defaultBoardOptions;

	const selectedBoard = boardOptions.find(boardOption => boardOption.id === board.id);

	const isSaving = updateOpportunityMutation.isPending || createOpportunityMutation.isPending;

	const renderFieldLabel = (label: string, required = false) => (
		<div className={css(styleSheet.fieldLabel)}>
			{label}
			{required ? <span className={css(styleSheet.requiredMark)}>*</span> : null}
		</div>
	);

	return (
		<Dialog
			open
			onOpenChange={open => {
				if (!open) onRequestClose(null, true)();
			}}
		>
			<DialogPopup styles={[styleSheet.popup]}>
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<div className={css(styleSheet.columns)}>
						{/* Required fields */}
						<div className={css(styleSheet.column)}>
							<div className={css(styleSheet.field)}>
								{renderFieldLabel('Primary Contact', true)}
								<InputFieldError errorMessage={primaryContactErrorMessage}>
									<Combobox
										items={contactItems}
										value={primaryContact ?? null}
										inputValue={contactSearch}
										onInputValueChange={setContactSearch}
										filter={null}
										itemToStringLabel={(contact: Api.IContact) => getDisplayName(contact)}
										isItemEqualToValue={(a: Api.IContact, b: Api.IContact) => a.id === b.id}
										onValueChange={(contact: Api.IContact | null) => onPrimaryContactSelected(contact)}
									>
										<ComboboxInput
											id='edit-opportunity-contact-input'
											placeholder='Search contacts'
											onFocus={onInputFocus}
											showClear
											autoFocus
										>
											<InputGroupAddon align='inlineStart'>
												<AssigneeIcon />
											</InputGroupAddon>
										</ComboboxInput>
										<ComboboxContent>
											<ComboboxEmpty>No contacts found</ComboboxEmpty>
											<ComboboxList>
												{(contact: Api.IContact) => (
													<ComboboxAvatarItem key={contact.id} value={contact}>
														<ComboboxAvatarItemAvatar imageUrl={contact.profilePic} fallbackText={initials(contact)} />
														<ComboboxAvatarItemTitle>{getDisplayName(contact)}</ComboboxAvatarItemTitle>
														<ComboboxAvatarItemDescription>{contact.primaryEmail?.value}</ComboboxAvatarItemDescription>
													</ComboboxAvatarItem>
												)}
											</ComboboxList>
											<div className={css(styleSheet.comboboxFooter)}>
												<AddEntityButtons
													type='contact'
													fragment={contactSearch}
													quickAddEntityVM={quickAddEntityVM}
													onEntityCreated={entity => onPrimaryContactSelected(entity as Api.IContact)}
												/>
											</div>
										</ComboboxContent>
									</Combobox>
								</InputFieldError>
							</div>

							<div className={css(styleSheet.field)}>
								{renderFieldLabel('Opportunity Name', true)}
								<InputFieldError errorMessage={nameErrorMessage}>
									<Input
										id='edit-opportunity-name-input'
										type='text'
										value={name || ''}
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
										onFocus={onInputFocus}
									/>
								</InputFieldError>
								<div className={css(styleSheet.helperText)}>
									This is the main title. It could be a project name or property name.
								</div>
							</div>

							<div className={css(styleSheet.field)}>
								{renderFieldLabel('Opportunity Owner', true)}
								<InputFieldError errorMessage={ownerErrorMessage}>
									<UserCombobox id={owner?.id} onValueChange={(user: Api.IUser | null) => onOwnerSelected(user)}>
										<ComboboxInput
											id='edit-opportunity-owner-input'
											placeholder='Search users'
											onFocus={onInputFocus}
											showClear
										>
											<InputGroupAddon align='inlineStart'>
												<AssigneeIcon />
											</InputGroupAddon>
										</ComboboxInput>
										<ComboboxContent>
											<UserComboboxStatus />
											<ComboboxList>
												{(user: Api.IUser) => <UserComboboxAvatarItem key={user.id} user={user} />}
											</ComboboxList>
										</ComboboxContent>
									</UserCombobox>
								</InputFieldError>
							</div>

							<div className={css(styleSheet.field)}>
								{renderFieldLabel('Opportunity Board', true)}
								{boardOptions.length ? (
									<Select
										value={selectedBoard?.id ?? ''}
										onValueChange={value => {
											const option = boardOptions.find(o => o.id === value);
											if (option) {
												onBoardSelected(option);
											}
										}}
									>
										<SelectTrigger styles={styleSheet.selectStyles}>
											<span>{selectedBoard?.text ?? 'Please select...'}</span>
										</SelectTrigger>
										<SelectContent>
											{boardOptions.map(option => (
												<SelectItem key={option.id} value={option.id}>
													{option.text}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								) : (
									<Input
										id='edit-opportunity-disabled-board'
										type='text'
										disabled
										readOnly
										value={selectedBoard.text}
									/>
								)}
							</div>

							<div className={css(styleSheet.field)}>
								{renderFieldLabel('Opportunity Stage', true)}
								{stageOptions ? (
									<Select
										value={selectedStageOption?.id ?? ''}
										onValueChange={value => {
											const option = stageOptions.find(o => o.id === value);
											if (option) {
												onStageSelected(option as ISelectOption<Api.OpportunitiesBoardStageViewModel>);
											}
										}}
									>
										<SelectTrigger styles={styleSheet.selectStyles}>
											<span>{selectedStageOption?.text ?? 'Please select...'}</span>
										</SelectTrigger>
										<SelectContent>
											{stageOptions.map(option => (
												<SelectItem key={option.id} value={option.id}>
													{option.text}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								) : (
									<InputFieldError errorMessage={stageErrorMessage}>
										<Input
											id='edit-opportunity-disabled-stage'
											type='text'
											disabled
											readOnly
											value={selectedStageOption.text}
										/>
									</InputFieldError>
								)}
							</div>
						</div>

						{/* Optional fields — the star on required fields implies these are optional */}
						<div className={css(styleSheet.column)}>
							<div className={css(styleSheet.field)}>
								{renderFieldLabel('Company')}
								<InputFieldError errorMessage={companyErrorMessage}>
									<Combobox
										items={companyItems}
										value={company ?? null}
										inputValue={companySearch}
										onInputValueChange={setCompanySearch}
										filter={null}
										itemToStringLabel={(c: Api.ICompany) => c.companyName ?? ''}
										isItemEqualToValue={(a: Api.ICompany, b: Api.ICompany) => a.id === b.id}
										onValueChange={(c: Api.ICompany | null) => onCompanySelected(c)}
									>
										<ComboboxInput
											id='edit-opportunity-company-input'
											placeholder='Search companies'
											onFocus={onInputFocus}
											showClear
										>
											<InputGroupAddon align='inlineStart'>
												<CompanyIcon />
											</InputGroupAddon>
										</ComboboxInput>
										<ComboboxContent>
											<ComboboxEmpty>No companies found</ComboboxEmpty>
											<ComboboxList>
												{(c: Api.ICompany) => (
													<ComboboxAvatarItem key={c.id} value={c}>
														<ComboboxAvatarItemAvatar
															imageUrl={c.logoUrl}
															fallbackText={c.companyName?.substring(0, 1)}
														/>
														<ComboboxAvatarItemTitle>{c.companyName}</ComboboxAvatarItemTitle>
													</ComboboxAvatarItem>
												)}
											</ComboboxList>
											<div className={css(styleSheet.comboboxFooter)}>
												<AddEntityButtons
													type='company'
													fragment={companySearch}
													quickAddEntityVM={quickAddEntityVM}
													onEntityCreated={entity => onCompanySelected(entity as Api.ICompany)}
												/>
											</div>
										</ComboboxContent>
									</Combobox>
								</InputFieldError>
							</div>

							<div className={css(styleSheet.field)}>
								{renderFieldLabel('Amount')}
								<InputFieldError errorMessage={amountErrorMessage}>
									<CurrencyInput
										id='edit-opportunity-amount-input'
										value={amount}
										onValueChange={setAmount}
										onFocus={onInputFocus}
									/>
								</InputFieldError>
							</div>

							<div className={css(styleSheet.field)}>
								{renderFieldLabel('Closing Date')}
								<InputFieldError errorMessage={closingDateErrorMessage}>
									<div className={css(styleSheet.closingDate)}>
										<div className={css(styleSheet.closingDateInput)}>
											<DateInput
												value={closeDate ? isoToDateInputFormat(closeDate.toISOString()) : ''}
												onChange={onCloseDateChange}
											/>
										</div>
										{closeDate ? (
											<Button
												variant='ghost'
												size='icon'
												onClick={() => setCloseDate(null)}
												aria-label='Clear closing date'
											>
												<ClearFieldIcon />
											</Button>
										) : null}
									</div>
								</InputFieldError>
							</div>

							<div className={css(styleSheet.field)}>
								{renderFieldLabel('Tags')}
								<div className={css(styleSheet.tagsField)}>
									<TagIcon
										className={css(
											baseStyleSheet.flexShrink0,
											baseStyleSheet.mr4,
											baseStyleSheet.mt3,
											baseStyleSheet.w4
										)}
										fillColor={THEME.colors.icon.gray}
									/>
									{(() => {
										// Use tagsCollection if available (when editing existing opportunity), otherwise create basic tag objects
										const editorTags =
											opportunity.tagsCollection && opportunity.tagsCollection.length > 0
												? opportunity.tagsCollection.filter(tag => tags.includes(tag.name))
												: tags.map(tagName => ({ name: tagName, id: tagName }));

										return (
											<TagsListWithTagsEditor
												tagType={Api.ResourceAutoCompleteViewModelType.AccountTag}
												className={css(baseStyleSheet.flex1)}
												disabled={false}
												entity={null}
												onRequestAddTag={onRequestAddTag}
												onRequestRemoveTag={onRequestRemoveTag}
												tags={editorTags}
											/>
										);
									})()}
								</div>
							</div>

							<div className={css(styleSheet.field)}>
								{renderFieldLabel('Details')}
								<div className={css(styleSheet.detailsField)}>
									<RichContentDocumentEditor
										contentClassName={css(styleSheet.details)}
										config={DefaultDetailsEditorConfig}
										contentState={detailsEditorState}
										onContentStateChanged={(newState: Api.IRichContentEditorState) => setDetailsEditorState(newState)}
										onFocus={onInputFocus}
									/>
								</div>
							</div>
						</div>
					</div>
				</DialogBody>
				<DialogFooter>
					<Button variant='secondary' onClick={onRequestClose(null, true)} disabled={isSaving}>
						Cancel
					</Button>
					<Button variant='primary' onClick={onSaveButtonClicked} isLoading={isSaving}>
						Save
					</Button>
				</DialogFooter>
			</DialogPopup>

			<Dialog
				open={quickAddEntityVM.isOpen}
				onOpenChange={open => {
					if (!open) quickAddEntityVM.close();
				}}
			>
				<DialogPopup>
					<QuickAddEntity quickAddEntityVm={quickAddEntityVM} />
				</DialogPopup>
			</Dialog>
		</Dialog>
	);
};

export const EditOpportunityModal = observer(_EditOpportunityModal);
